import {
    type ProductData,
    type RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import { convertSnakeCaseToUpperCase } from "@/lib/utils"

/**
 *
 * @param products
 * @returns returns a boolean value based on the availability of all the relatedVariants
 */
export function areAllVariantsOutOfStock(
    relatedVariants: RelatedVariant[] = []
): boolean {
    return relatedVariants.some(item => !item.available)
}

/**
 * @param relatedVariants
 * @returns returns a sorterd variant list based on the sort order and availability
 */
export function getSortedColorSwatches(relatedVariants: RelatedVariant[]) {
    return relatedVariants
        .filter(variant => variant?.color_swatch?.hex)
        .sort((a, b) => {
            const orderA = Number(a.variant_sort_order_sort_order) || Infinity
            const orderB = Number(b.variant_sort_order_sort_order) || Infinity
            if (orderA !== orderB) {
                return orderA - orderB
            }
            if (a.available === b.available) {
                return 0
            }
            return a.available ? -1 : 1
        })
}

export const isFullRelatedVariant = (
    relatedVariant: RelatedVariant
): relatedVariant is RelatedVariant =>
    (relatedVariant as RelatedVariant).color_swatch !== undefined

export const extractRelatedVariants = (
    relatedVariants: RelatedVariant[]
): RelatedVariant[] => {
    if (!Array.isArray(relatedVariants) || relatedVariants.length === 0) {
        return []
    }
    const seenProducts = new Set<string>()
    return relatedVariants.filter(
        relatedVariant =>
            isFullRelatedVariant(relatedVariant) &&
            relatedVariant.relation === "variant" &&
            relatedVariant.price !== null &&
            !seenProducts.has(
                `${relatedVariant.color_swatch?.color_name}-${relatedVariant.uri}`
            ) &&
            seenProducts.add(
                `${relatedVariant.color_swatch?.color_name}-${relatedVariant.uri}`
            )
    )
}

export const transformProductData = (product: ProductData) => {
    const lifestyleImages =
        Array.isArray(product?.media) && product.media.length > 0
            ? product.media.slice(4)
            : []

    const relatedVariants = product.relatedVariants
        ? extractRelatedVariants(product.relatedVariants)
        : []

    const productFitType: string = convertSnakeCaseToUpperCase(
        product?.fit_type_fit_type || ""
    )

    const relatedVariantsFitType = relatedVariants.length
        ? Array.from(
              new Set(
                  relatedVariants.reduce<string[]>((acc, prod) => {
                      if (prod.fit_type_fit_type) {
                          acc.push(prod.fit_type_fit_type)
                      }
                      return acc
                  }, [])
              )
          ).sort()
        : []

    return {
        ...product,
        relatedVariants,
        lifestyleImages,
        productFitType,
        relatedVariantsFitType
    }
}
